import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

// Module-level flags to prevent multiple initializations across component instances
// NEVER reset these during the session
let adminPageInitialized = false;
let adminPageInitInProgress = false;
let adminPageApiCallsMade = false; // Track if API calls have been made (never reset)

const AdminPage = ({ showNotification }) => {
  const [apiKey, setApiKey] = useState(localStorage.getItem('admin_api_key') || '');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const initRef = useRef(false);

  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'user' });
  const [tUrl1, setTUrl1] = useState('');
  const [tUrl2, setTUrl2] = useState('');
  const [tLoading, setTLoading] = useState(false);
  const [tResult, setTResult] = useState(null);
  const [f1, setF1] = useState(null);
  const [f2, setF2] = useState(null);
  const [fLoading, setFLoading] = useState(false);
  const [fResult, setFResult] = useState(null);

  const client = axios.create({
    baseURL: 'https://outiloeuvresvolees.inouiagency.com',
  });

  const headers = apiKey ? { 'X-Admin-Key': apiKey } : {};

  const loadUsers = useCallback(async () => {
    if (!apiKey) return;
    try {
      console.log('[AdminPage] Calling loadUsers...');
      setLoading(true);
      const res = await client.get('/api/admin/users', { headers });
      if (res.data.success) {
        setUsers(res.data.users || []);
        console.log('[AdminPage] Loaded users:', res.data.users?.length || 0);
      } else {
        showNotification && showNotification(res.data.error || 'Failed to load users', 'error');
      }
    } catch (e) {
      console.error('[AdminPage] Error loading users:', e);
      showNotification && showNotification('Unauthorized or network error', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiKey, showNotification]);

  useEffect(() => {
    // Always fetch data when AdminPage component mounts
    // Reset init flag to allow fresh initialization on each mount
    initRef.current = false;
    
    // If another instance is initializing, wait a bit and try again
    if (adminPageInitInProgress) {
      console.log('[AdminPage] Initialization in progress, will retry...');
      const timeout = setTimeout(() => {
        if (!adminPageInitInProgress && apiKey) {
          loadUsers();
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
    
    adminPageInitialized = true;
    adminPageInitInProgress = true;
    adminPageApiCallsMade = true; // Mark as done - NEVER reset this
    initRef.current = true;
    
    console.log('[AdminPage] Initializing and loading users...');
    if (apiKey) {
      loadUsers().finally(() => {
        adminPageInitInProgress = false;
      });
    } else {
      adminPageInitInProgress = false;
    }
    
    // Reset ref on unmount so next mount can initialize
    return () => {
      initRef.current = false;
    };
  }, [apiKey, loadUsers]);

  const saveKey = () => {
    localStorage.setItem('admin_api_key', apiKey);
    loadUsers();
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      showNotification && showNotification('All fields required', 'error');
      return;
    }
    try {
      const res = await client.post('/api/admin/users', form, { headers });
      if (res.data.success) {
        showNotification && showNotification('User created', 'success');
        setForm({ name: '', email: '', phone: '', password: '', role: 'user' });
        loadUsers();
      } else {
        showNotification && showNotification(res.data.error || 'Create failed', 'error');
      }
    } catch (e) {
      showNotification && showNotification('Unauthorized or error', 'error');
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await client.delete(`/api/admin/users/${id}`, { headers });
      if (res.data.success) {
        showNotification && showNotification('User deleted', 'success');
        setUsers((u) => u.filter((x) => x.id !== id));
      } else {
        showNotification && showNotification(res.data.error || 'Delete failed', 'error');
      }
    } catch (e) {
      showNotification && showNotification('Unauthorized or error', 'error');
    }
  };

  const updateUser = async (id, patch) => {
    try {
      const res = await client.patch(`/api/admin/users/${id}`, patch, { headers });
      if (res.data.success) {
        showNotification && showNotification('User updated', 'success');
        loadUsers();
      } else {
        showNotification && showNotification(res.data.error || 'Update failed', 'error');
      }
    } catch (e) {
      showNotification && showNotification('Unauthorized or error', 'error');
    }
  };

  const testCompare = async () => {
    if (!apiKey) {
      showNotification && showNotification('Set Admin API Key first', 'error');
      return;
    }
    if (!tUrl1 || !tUrl2) {
      showNotification && showNotification('Provide two image URLs', 'error');
      return;
    }
    try {
      setTLoading(true);
      setTResult(null);
      const res = await client.post('/api/admin/test-compare', { url1: tUrl1, url2: tUrl2 }, { headers });
      if (res.data.success) {
        setTResult(res.data.similarity);
      } else {
        showNotification && showNotification(res.data.error || 'Test compare failed', 'error');
      }
    } catch (e) {
      console.error('[AdminPage] test-compare error:', e);
      showNotification && showNotification('Network or authorization error', 'error');
    } finally {
      setTLoading(false);
    }
  };

  const testCompareUpload = async () => {
    if (!apiKey) {
      showNotification && showNotification('Set Admin API Key first', 'error');
      return;
    }
    if (!f1 || !f2) {
      showNotification && showNotification('Select two files', 'error');
      return;
    }
    try {
      setFLoading(true);
      setFResult(null);
      const fd = new FormData();
      fd.append('file1', f1);
      fd.append('file2', f2);
      const res = await client.post('/api/admin/test-compare-upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        setFResult(res.data.similarity);
      } else {
        showNotification && showNotification(res.data.error || 'Test compare upload failed', 'error');
      }
    } catch (e) {
      console.error('[AdminPage] test-compare-upload error:', e);
      showNotification && showNotification('Network or authorization error', 'error');
    } finally {
      setFLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Admin: User Management</h1>
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin API Key</label>
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Enter X-Admin-Key" />
          </div>
          <button onClick={saveKey} className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">Save & Load</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Create User</h2>
          <form onSubmit={createUser} className="space-y-3">
            <input placeholder="Name" className="w-full border border-gray-300 rounded-md px-3 py-2" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} />
            <input type="email" placeholder="Email" className="w-full border border-gray-300 rounded-md px-3 py-2" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
            <input placeholder="Phone" className="w-full border border-gray-300 rounded-md px-3 py-2" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} />
            <input type="password" placeholder="Password" className="w-full border border-gray-300 rounded-md px-3 py-2" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} />
            <select className="w-full border border-gray-300 rounded-md px-3 py-2" value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})}>
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
            <button type="submit" className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700">Create</button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Users {loading && <span className="text-xs text-gray-500">(loading...)</span>}</h2>
            <button onClick={loadUsers} className="px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50">Refresh</button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Phone</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Active</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b">
                    <td className="py-2 pr-4">{u.name}</td>
                    <td className="py-2 pr-4">{u.email}</td>
                    <td className="py-2 pr-4">{u.phone}</td>
                    <td className="py-2 pr-4">
                      <select value={u.role} onChange={(e)=>updateUser(u.id,{ role: e.target.value })} className="border border-gray-300 rounded px-2 py-1">
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td className="py-2 pr-4">
                      <input type="checkbox" checked={!!u.is_active} onChange={(e)=>updateUser(u.id,{ is_active: e.target.checked })} />
                    </td>
                    <td className="py-2 space-x-2">
                      <button onClick={()=>deleteUser(u.id)} className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Test Comparison</h2>
          <div className="space-y-3">
            <input className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Image URL 1" value={tUrl1} onChange={(e)=>setTUrl1(e.target.value)} />
            <input className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="Image URL 2" value={tUrl2} onChange={(e)=>setTUrl2(e.target.value)} />
            <div className="flex items-center space-x-3">
              <button onClick={testCompare} disabled={tLoading} className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">{tLoading ? 'Comparing...' : 'Run Test'}</button>
              {tResult !== null && (
                <span className="text-sm text-gray-700">Similarity: <span className="font-semibold">{tResult.toFixed(3)}</span></span>
              )}
            </div>
            <p className="text-xs text-gray-500">Uses the same engine as real search (hash + ORB + subimage). URLs can be http(s) or file:// paths accessible to the server.</p>
            <div className="mt-6 border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Or upload two images</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input type="file" accept="image/*" onChange={(e)=>setF1(e.target.files?.[0] || null)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                <input type="file" accept="image/*" onChange={(e)=>setF2(e.target.files?.[0] || null)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              <div className="flex items-center space-x-3 mt-3">
                <button onClick={testCompareUpload} disabled={fLoading} className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">{fLoading ? 'Comparing...' : 'Run Upload Test'}</button>
                {fResult !== null && (
                  <span className="text-sm text-gray-700">Similarity: <span className="font-semibold">{fResult.toFixed(3)}</span></span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;


